import { Button, Card, Text, TextArea } from "@astryxdesign/core";
import * as stylex from "@stylexjs/stylex";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { AppShell } from "@/components/app-shell";
import { api } from "@/lib/api";
import { useSession } from "@/services/auth0/session";

type ShiftType = "MORNING" | "AFTERNOON" | "EVENING";
type Day = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
type WeeklyAvailability = Record<Day, ShiftType[]>;
type AvailabilityRequest = {
  id: string;
  weekStart: string;
  message: string | null;
  status: "OPEN" | "CLOSED" | "CANCELLED";
  dispatchCenter: { id: string; name: string };
};
type Submission = {
  id: string;
  weekStart: string;
  availability: WeeklyAvailability;
  notes: string | null;
  request: AvailabilityRequest | null;
};
type Shift = {
  id: string;
  date: string;
  shiftType: ShiftType;
  startTime: string | null;
  endTime: string | null;
  status: "SCHEDULED" | "CONFIRMED" | "COMPLETED" | "CANCELLED";
};

const DAYS: { key: Day; label: string }[] = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];
const SHIFT_TYPES: { key: ShiftType; label: string }[] = [
  { key: "MORNING", label: "Morning · 06:00–12:00" },
  { key: "AFTERNOON", label: "Afternoon · 12:00–18:00" },
  { key: "EVENING", label: "Evening · 18:00–24:00" },
];

const styles = stylex.create({
  back: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    marginBottom: 14,
    color: "var(--color-text-secondary)",
    textDecoration: "none",
  },
  stack: { display: "grid", gap: 18 },
  section: { display: "grid", gap: 9 },
  card: { display: "grid", gap: 10 },
  row: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  day: { borderTop: "1px solid var(--color-border)", paddingBlock: 10 },
  shiftOptions: { display: "flex", gap: 12, flexWrap: "wrap", marginTop: 5 },
  option: { display: "inline-flex", gap: 5, alignItems: "center" },
  error: { color: "var(--color-error)" },
  success: { color: "var(--color-success)" },
  list: { display: "grid", gap: 8, listStyle: "none", margin: 0, padding: 0 },
});

function datePart(iso: string): string {
  return iso.slice(0, 10);
}

function addCalendarDays(date: string, days: number): string {
  const value = new Date(`${date}T00:00:00.000Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

function todayInZone(timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const part = (type: string) =>
    parts.find((item) => item.type === type)?.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}

function displayDate(date: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(new Date(`${date}T12:00:00.000Z`));
}

function emptyAvailability(): WeeklyAvailability {
  return { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] };
}

function AvailabilityForm({
  request,
  submission,
  onSaved,
}: {
  request: AvailabilityRequest;
  submission?: Submission;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const [availability, setAvailability] = useState<WeeklyAvailability>(
    submission?.availability ?? emptyAvailability()
  );
  const [notes, setNotes] = useState(submission?.notes ?? "");
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api.post("/api/v2/operator/availability", {
        requestId: request.id,
        weekStart: datePart(request.weekStart),
        availability,
        notes: notes.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["operator-scheduling"] });
      onSaved();
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof Error ? cause.message : "Could not save availability"
      );
    },
  });

  const toggle = (day: Day, shift: ShiftType) => {
    setAvailability((current) => ({
      ...current,
      [day]: current[day].includes(shift)
        ? current[day].filter((item) => item !== shift)
        : [...current[day], shift],
    }));
  };

  return (
    <Card padding={4}>
      <div {...stylex.props(styles.card)}>
        <Text weight="semibold">
          {submission ? "Edit" : "Submit"} availability · week of{" "}
          {displayDate(datePart(request.weekStart))}
        </Text>
        {request.message ? (
          <Text size="sm" color="secondary">
            {request.message}
          </Text>
        ) : null}
        {DAYS.map((day, index) => (
          <div key={day.key} {...stylex.props(styles.day)}>
            <Text weight="medium">
              {day.label} ·{" "}
              {displayDate(addCalendarDays(datePart(request.weekStart), index))}
            </Text>
            <div {...stylex.props(styles.shiftOptions)}>
              {SHIFT_TYPES.map((shift) => (
                <label key={shift.key} {...stylex.props(styles.option)}>
                  <input
                    type="checkbox"
                    checked={availability[day.key].includes(shift.key)}
                    onChange={() => toggle(day.key, shift.key)}
                  />
                  <span>{shift.label}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
        <TextArea label="Notes (optional)" value={notes} onChange={setNotes} />
        {error ? (
          <span role="alert" {...stylex.props(styles.error)}>
            {error}
          </span>
        ) : null}
        <Button
          variant="primary"
          label={submission ? "Update availability" : "Submit availability"}
          isLoading={save.isPending}
          isDisabled={save.isPending}
          onClick={() => save.mutate()}
        />
      </div>
    </Card>
  );
}

export function ScheduleScreen() {
  const { operatorId, operator } = useSession();
  const [editing, setEditing] = useState<{
    request: AvailabilityRequest;
    submission?: Submission;
  } | null>(null);
  const [savedMessage, setSavedMessage] = useState(false);
  const timeZone = operator?.dispatchCenter?.timeZone;
  const today = timeZone ? todayInZone(timeZone) : null;
  const endDate = today ? addCalendarDays(today, 6) : null;

  const pending = useQuery({
    queryKey: ["operator-scheduling", "pending", operatorId],
    queryFn: async () =>
      (
        await api.get<AvailabilityRequest[]>(
          "/api/v2/operator/availability/pending"
        )
      ).data,
    enabled: !!operatorId,
  });
  const submitted = useQuery({
    queryKey: ["operator-scheduling", "submitted", operatorId],
    queryFn: async () =>
      (await api.get<Submission[]>("/api/v2/operator/availability/submitted"))
        .data,
    enabled: !!operatorId,
  });
  const shifts = useQuery({
    queryKey: ["operator-scheduling", "shifts", operatorId, today, endDate],
    queryFn: async () =>
      (
        await api.get<Shift[]>("/api/v2/operator/shifts", {
          params: { startDate: today, endDate },
        })
      ).data,
    enabled: !!operatorId && !!today && !!endDate,
  });
  const upcoming = (shifts.data ?? [])
    .filter((shift) => shift.status !== "CANCELLED")
    .slice(0, 3);

  return (
    <AppShell>
      <Link to="/" {...stylex.props(styles.back)}>
        <ArrowLeft size={16} /> My trips
      </Link>
      <div {...stylex.props(styles.stack)}>
        <Text as="h1" size="lg" weight="semibold">
          My schedule
        </Text>
        {savedMessage ? (
          <span role="status" {...stylex.props(styles.success)}>
            Availability saved
          </span>
        ) : null}
        <section {...stylex.props(styles.section)}>
          <Text as="h2" weight="semibold">
            Availability requests
          </Text>
          {pending.isLoading || submitted.isLoading ? (
            <Text size="sm">Loading availability…</Text>
          ) : null}
          {pending.isError || submitted.isError ? (
            <span role="alert" {...stylex.props(styles.error)}>
              Could not load availability. Refresh to try again.
            </span>
          ) : null}
          {!pending.isLoading &&
          !submitted.isLoading &&
          !pending.isError &&
          !submitted.isError &&
          !pending.data?.length &&
          !submitted.data?.length ? (
            <Text size="sm" color="secondary">
              No availability requests yet.
            </Text>
          ) : null}
          {pending.data?.map((request) => (
            <Card key={request.id} padding={4}>
              <div {...stylex.props(styles.card)}>
                <Text weight="medium">
                  Week of {displayDate(datePart(request.weekStart))} ·{" "}
                  {request.dispatchCenter?.name}
                </Text>
                {request.message ? (
                  <Text size="sm" color="secondary">
                    {request.message}
                  </Text>
                ) : null}
                <Button
                  variant="secondary"
                  label="Submit availability"
                  onClick={() => {
                    setSavedMessage(false);
                    setEditing({ request });
                  }}
                />
              </div>
            </Card>
          ))}
          {submitted.data?.map((submission) => (
            <Card key={submission.id} padding={4}>
              <div {...stylex.props(styles.card)}>
                <Text weight="medium">
                  Submitted · week of{" "}
                  {displayDate(datePart(submission.weekStart))}
                </Text>
                <Text size="sm" color="secondary">
                  {submission.request?.status === "OPEN"
                    ? "Editable while request is open"
                    : `Locked · ${submission.request?.status ?? "request unavailable"}`}
                </Text>
                {DAYS.map((day) => (
                  <Text key={day.key} size="sm">
                    {day.label}:{" "}
                    {submission.availability[day.key].length > 0
                      ? submission.availability[day.key]
                          .map((shift) => shift.toLowerCase())
                          .join(", ")
                      : "Unavailable"}
                  </Text>
                ))}
                {submission.notes ? (
                  <Text size="sm" color="secondary">
                    Notes: {submission.notes}
                  </Text>
                ) : null}
                {submission.request?.status === "OPEN" ? (
                  <Button
                    variant="secondary"
                    label="Edit availability"
                    onClick={() => {
                      setSavedMessage(false);
                      if (submission.request) {
                        setEditing({ request: submission.request, submission });
                      }
                    }}
                  />
                ) : null}
              </div>
            </Card>
          ))}
          {editing ? (
            <AvailabilityForm
              key={`${editing.request.id}-${editing.submission?.id ?? "new"}`}
              request={editing.request}
              submission={editing.submission}
              onSaved={() => {
                setEditing(null);
                setSavedMessage(true);
              }}
            />
          ) : null}
        </section>
        <section {...stylex.props(styles.section)}>
          <Text as="h2" weight="semibold">
            Upcoming shifts
          </Text>
          {!timeZone ? (
            <span role="alert" {...stylex.props(styles.error)}>
              Dispatch center timezone is unavailable.
            </span>
          ) : null}
          {shifts.isLoading ? <Text size="sm">Loading shifts…</Text> : null}
          {shifts.isError ? (
            <span role="alert" {...stylex.props(styles.error)}>
              Could not load shifts. Refresh to try again.
            </span>
          ) : null}
          {timeZone &&
          !shifts.isLoading &&
          !shifts.isError &&
          upcoming.length === 0 ? (
            <Text size="sm" color="secondary">
              No shifts in the next seven days.
            </Text>
          ) : null}
          {upcoming.length > 0 ? (
            <ul {...stylex.props(styles.list)}>
              {upcoming.map((shift) => (
                <li key={shift.id}>
                  <Card padding={4}>
                    <div {...stylex.props(styles.row)}>
                      <Text weight="medium">
                        {displayDate(datePart(shift.date))}
                      </Text>
                      <Text size="sm">
                        {shift.shiftType.toLowerCase()}{" "}
                        {shift.startTime
                          ? `· ${shift.startTime}${shift.endTime ? `–${shift.endTime}` : ""}`
                          : ""}
                      </Text>
                      <Text size="sm" color="secondary">
                        {shift.status}
                      </Text>
                    </div>
                  </Card>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      </div>
    </AppShell>
  );
}
